import React, { useState } from 'react';

interface SupportTicket {
  id: string;
  name: string;
  email: string;
  message: string;
  createdAt: string;
}

function saveLocal(ticket: SupportTicket) {
  try {
    const key = 'supportTickets';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.unshift(ticket);
    localStorage.setItem(key, JSON.stringify(existing));
  } catch {}
}

async function submitSupport(ticket: Omit<SupportTicket, 'id' | 'createdAt'>) {
  const payload: SupportTicket = {
    ...ticket,
    id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
    createdAt: new Date().toISOString(),
  };

  try {
    const res = await fetch('/api/support', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Server error');
  } catch {
    // Fallback to localStorage
    saveLocal(payload);
  }

  return payload;
}

const SupportPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) return;
    setLoading(true);
    await submitSupport({ name, email, message });
    setLoading(false);
    setSent(true);
    setName('');
    setEmail('');
    setMessage('');
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3">Contact Support</h1>
      <p className="text-sm md:text-base text-gray-600 mb-6">Tell us what went wrong or what you need help with. We’ll get back to you shortly.</p>

      {sent && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800 text-sm md:text-base">
          Your complaint has been sent. We will contact you shortly.
        </div>
      )}

      <form onSubmit={onSubmit} className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="you@example.com" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs md:text-sm text-gray-700 mb-1">Complaint</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Describe your issue"></textarea>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button type="submit" disabled={loading} className="inline-flex items-center rounded-md bg-blue-600 text-white px-4 py-2 text-sm md:text-base hover:bg-blue-700 disabled:opacity-60">
            {loading ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupportPage;